import type { StatusChipVariant } from '@/components/ui/status-chip';
import type { AbuseAlertState, AbuseSubject, AbuseSubjectKey, AbuseSubjectType } from '@/services/api/abuse';
import { formatSnakeCase } from '@/utils/string';

export const SUBJECT_TYPE_LABELS: Record<AbuseSubjectType, string> = { user: 'Accounts', apiKey: 'API keys', ip: 'IPs' };

export const SUBJECT_TYPE_SINGULAR: Record<AbuseSubjectType, string> = { user: 'Account', apiKey: 'API key', ip: 'IP' };

// Record<string, string> on purpose: fast-api adds event types faster than this UI ships, so unknown
// values render through the formatSnakeCase fallback
export const EVENT_TYPE_LABELS: Record<string, string> = {
  payload_schema_violation: 'Payload schema violation',
  insufficient_funds: 'Insufficient funds',
  unknown_endpoint_probe: 'Unknown endpoint probe',
  verification_code_mismatch: 'Verification code mismatch',
  no_balance_probe: 'No-balance probe',
  ineligible_account_action: 'Ineligible account action',
  invalid_document_upload: 'Invalid document upload',
  contract_violation: 'Contract violation',
  auth_signature_failure: 'Auth signature failure',
  malicious_upload: 'Malicious upload',
};

export const formatEventType = (value: string): string => EVENT_TYPE_LABELS[value] ?? formatSnakeCase(value);

export const ALERT_STATE_LABELS: Record<AbuseAlertState, string> = { idle: 'Idle', triggered: 'Triggered', case_open: 'Case open', escalated: 'Escalated' };

export const ALERT_STATE_CHIP_VARIANTS: Record<AbuseAlertState, StatusChipVariant> = { idle: 'muted', triggered: 'warning', case_open: 'warning', escalated: 'destructive' };

export const formatScore = (score: number): string => score.toFixed(1);

/** A null subject is an expired score with events still retained; it reads as history, as an idle score does */
export const getSubjectChip = (subject: AbuseSubject | null): { label: string; variant: StatusChipVariant } => {
  if (!subject || subject.alertState === 'idle') return { label: 'Abuse history', variant: 'muted' };

  return { label: `Abuse ${ALERT_STATE_LABELS[subject.alertState].toLowerCase()}`, variant: ALERT_STATE_CHIP_VARIANTS[subject.alertState] };
};

/** Score colour against the scoring thresholds the summary reports */
export const getScoreClass = (score: number, threshold: number, escalationThreshold: number): string => {
  if (score >= escalationThreshold) return 'text-red-500';
  if (score >= threshold) return 'text-amber-500';
  return 'text-muted-foreground';
};

// Query params, not a path segment: IPv6 subject ids contain '/' (2001:db8::/64)
export const abuseSubjectPath = ({ subjectType, subjectId }: AbuseSubjectKey): string =>
  `/compliance/abuse/subject?${new URLSearchParams({ type: subjectType, id: subjectId }).toString()}`;

export const parseSubjectKey = (searchParams: URLSearchParams): AbuseSubjectKey | null => {
  const subjectType = searchParams.get('type');
  const subjectId = searchParams.get('id');

  if (!subjectId || (subjectType !== 'user' && subjectType !== 'apiKey' && subjectType !== 'ip')) return null;
  return { subjectType, subjectId };
};

/** "+12% vs previous period", or null when there is no comparable previous figure */
export const formatChangeVsPrevious = (current: number, previous: number | null): string | null => {
  if (previous === null) return null;
  if (previous === 0) return current === 0 ? 'No change vs previous period' : 'New vs previous period';
  const change = Math.round(((current - previous) / previous) * 100);

  return `${change > 0 ? '+' : ''}${change}% vs previous period`;
};

const DAY_LABEL_FORMAT = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });

/** "08 Oct" for a YYYY-MM-DD UTC day key */
export const formatDayLabel = (date: string): string => DAY_LABEL_FORMAT.format(new Date(`${date}T00:00:00.000Z`));

export const EVENTS_COLOR = 'hsl(var(--primary))';
// Under 3:1 on the light surface, so crossing markers always carry a text label
export const CROSSING_COLOR = 'hsl(var(--warning))';

export type WeightBand = 'low' | 'medium' | 'high';

/** Bands follow the scoring weights: noise (≤4), probing (8–13), hostile (≥26, one or two cross the threshold) */
export const getWeightBand = (weight: number): WeightBand => {
  if (weight >= 26) return 'high';
  if (weight >= 8) return 'medium';
  return 'low';
};

export const WEIGHT_BAND_CHIP_VARIANTS: Record<WeightBand, StatusChipVariant> = { low: 'muted', medium: 'warning', high: 'destructive' };

export interface TypeContribution {
  eventType: string;
  weight: number;
  events: number;
  /** events × weight: how much the type pushed scores, before decay */
  contribution: number;
}

/** Ranks event types by what they contributed to scores rather than by raw frequency */
export const rankByContribution = (byType: Array<{ eventType: string; weight: number; events: number }>): TypeContribution[] =>
  byType.map((row) => ({ ...row, contribution: row.events * row.weight })).sort((a, b) => b.contribution - a.contribution || b.weight - a.weight);

export const formatShare = (part: number, total: number): string => (total === 0 ? '0%' : `${Math.round((part / total) * 100)}%`);
